import { useState, useEffect } from 'react';
import type { StorageLocation, StorageLocationType, CreateStorageLocationInput } from '@maker-toolchest/shared';
import { STORAGE_LOCATION_TYPES } from '@maker-toolchest/shared';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { Button } from '@/components/Button';

export interface StorageLocationFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateStorageLocationInput) => void;
  location?: StorageLocation | null;
  loading?: boolean;
}

const typeOptions = STORAGE_LOCATION_TYPES.map((type) => ({
  value: type,
  label: type.charAt(0) + type.slice(1).toLowerCase(),
}));

export function StorageLocationForm({
  open,
  onClose,
  onSubmit,
  location,
  loading = false,
}: StorageLocationFormProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<StorageLocationType>('OTHER');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!location;

  // Reset form when opened or location changes
  useEffect(() => {
    if (open) {
      if (location) {
        setName(location.name);
        setType(location.type);
        setDescription(location.description || '');
        setCapacity(location.capacity?.toString() || '');
      } else {
        setName('');
        setType('OTHER');
        setDescription('');
        setCapacity('');
      }
      setErrors({});
    }
  }, [open, location]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (capacity && (isNaN(parseInt(capacity)) || parseInt(capacity) < 1)) {
      newErrors.capacity = 'Capacity must be a positive number';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      name: name.trim(),
      type,
      description: description.trim() || null,
      capacity: capacity ? parseInt(capacity) : null,
    });
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Storage Location' : 'Add Storage Location'}
      description={isEditing ? 'Update the storage location details.' : 'Create a new storage location for your spools.'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="storage-location-form" loading={loading}>
            {isEditing ? 'Save Changes' : 'Create Location'}
          </Button>
        </>
      }
    >
      <form id="storage-location-form" onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Top Shelf, Dryer Box 1"
          error={errors.name}
          autoFocus
        />

        <Select
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value as StorageLocationType)}
          options={typeOptions}
        />

        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional notes about this location..."
          rows={3}
        />

        <Input
          label="Capacity"
          type="number"
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          placeholder="Max number of spools (optional)"
          min={1}
          max={1000}
          error={errors.capacity}
          hint="Leave empty for unlimited"
        />
      </form>
    </SlideOver>
  );
}
